import { useState, useEffect, useCallback } from 'react'
import { Tag, Divider, Empty, Spin, Pagination, Button, message } from 'antd'
import {
    ClockCircleOutlined, FileOutlined, DownloadOutlined, PaperClipOutlined,
} from '@ant-design/icons'
import {
    getStockFilings,
    getStockNews,
    getStockDividends,
    getStockCorporateActions,
    getFilingFiles,
    getFilingDownloadUrl,
    getNewsDownloadUrl,
} from '@/api'
import type {
    FilingItem,
    NewsItem,
    DividendItem,
    CorporateActionItem,
    FilingFileURL,
} from '@/api'
import './index.scss'

type SourceType = 'filing' | 'news' | 'dividend' | 'corp_action'

const SOURCE_TABS: Array<{ label: string; value: SourceType }> = [
    { label: '公告', value: 'filing' },
    { label: '新闻', value: 'news' },
    { label: '分红', value: 'dividend' },
    { label: '除权复权', value: 'corp_action' },
]

const FILING_TYPE_LABELS: Record<number, string> = {
    1: '业绩', 2: '交易', 3: '股权', 4: '重大事项', 5: '其他',
}

const PAGE_SIZE = 10

interface TimelineEvent {
    id: string
    date: string
    time: string
    source: SourceType
    sourceLabel: string
    sub_title: string
    content: string
    tags: string[]
    /** 公告 id（用于拉取附件）；新闻 id（用于下载正文） */
    entityId?: number
}

// ── 格式化辅助 ──────────────────────────────────────────────

function toDate(s: string | null | undefined): string {
    if (!s) return ''
    return s.slice(0, 10)
}
function toDateShort(s: string): string {
    return s.slice(5) // MM-DD
}

// ── 映射函数 ────────────────────────────────────────────────

function mapFilings(items: FilingItem[]): TimelineEvent[] {
    return items.map((f) => {
        const date = toDate(f.published_at)
        const typeLabel = f.filing_type ? FILING_TYPE_LABELS[f.filing_type] ?? '公告' : '公告'
        const tags: string[] = [typeLabel]
        if (f.filing_source_type) tags.push(f.filing_source_type)
        return {
            id: `filing-${f.id}`,
            date, time: toDateShort(date),
            source: 'filing', sourceLabel: '公告',
            sub_title: f.title ?? '(无标题)',
            content: f.abstract ?? '',
            tags,
            entityId: f.id,
        }
    })
}

function mapNews(items: NewsItem[]): TimelineEvent[] {
    return items.map((n) => {
        const date = toDate(n.published_at)
        const tags: string[] = []
        if (n.source) tags.push(n.source)
        return {
            id: `news-${n.id}`,
            date, time: toDateShort(date),
            source: 'news', sourceLabel: '新闻',
            sub_title: n.title ?? '(无标题)',
            content: n.abstract ?? '',
            tags,
            entityId: n.id,
        }
    })
}

function mapDividends(items: DividendItem[]): TimelineEvent[] {
    return items
        .filter((d) => d.ex_dividend_date)
        .map((d, i) => {
            const date = toDate(d.ex_dividend_date)
            const perShare = d.dividend_per_share ?? 0
            const cur = d.currency ?? ''
            const tags: string[] = ['分红']
            if (d.fiscal_year) tags.push(d.fiscal_year)
            return {
                id: `dividend-${d.ex_dividend_date}-${i}`,
                date, time: toDateShort(date),
                source: 'dividend', sourceLabel: '分红',
                sub_title: `每股派息 ${perShare} ${cur}`.trim(),
                content: d.statement ?? '',
                tags,
            }
        })
}

function mapCorpActions(items: CorporateActionItem[]): TimelineEvent[] {
    return items
        .filter((c) => c.ex_div_date)
        .map((c, i) => {
            const date = toDate(c.ex_div_date)
            let parsedTypes: string[] = []
            if (c.action_types) {
                try { parsedTypes = JSON.parse(c.action_types) } catch { parsedTypes = [] }
            }
            return {
                id: `corp-${c.ex_div_date}-${i}`,
                date, time: toDateShort(date),
                source: 'corp_action', sourceLabel: '除权复权',
                sub_title: c.desc_sc ?? '除权复权事件',
                content: c.desc_sc ?? c.desc_en ?? '',
                tags: parsedTypes.length ? parsedTypes : ['除权复权'],
            }
        })
}

// ── 公告附件展开子组件 ──────────────────────────────────────

function FilingFiles({ filingId }: { filingId: number }) {
    const [files, setFiles] = useState<FilingFileURL[] | null>(null)
    const [loading, setLoading] = useState(false)

    const loadFiles = useCallback(async () => {
        setLoading(true)
        try {
            const resp = await getFilingFiles(filingId)
            setFiles(resp.filing_file_urls ?? [])
        } catch {
            setFiles([])
        } finally {
            setLoading(false)
        }
    }, [filingId])

    useEffect(() => { loadFiles() }, [loadFiles])

    const handleDownload = async (fileId: number) => {
        try {
            const url = await getFilingDownloadUrl(fileId)
            window.open(url, '_blank')
        } catch {
            message.error('附件下载失败')
        }
    }

    if (loading) return <div className="time-line__files-loading"><Spin size="small" /></div>
    if (!files) return null
    if (files.length === 0) return <div className="time-line__files-empty">暂无附件</div>

    return (
        <div className="time-line__files">
            {files.map((f, idx) => (
                <div key={idx} className="time-line__files-item">
                    <PaperClipOutlined style={{ marginRight: 6, color: 'var(--text-3)' }} />
                    <span className="time-line__files-name">{f.file_name ?? '未知文件'}</span>
                    {f.filing_file_id != null ? (
                        <Button
                            type="link"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(f.filing_file_id!)}
                            className="time-line__files-download"
                        >
                            下载
                        </Button>
                    ) : f.source_url ? (
                        <Button
                            type="link"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => window.open(f.source_url, '_blank')}
                            className="time-line__files-download"
                        >
                            查看原文
                        </Button>
                    ) : null}
                </div>
            ))}
        </div>
    )
}

// ── 时间线条目 ──────────────────────────────────────────────

function TimeLineItem({ event }: { event: TimelineEvent }) {
    const [expanded, setExpanded] = useState(false)

    const handleNewsDownload = async () => {
        if (event.entityId == null) return
        try {
            const url = await getNewsDownloadUrl(event.entityId)
            window.open(url, '_blank')
        } catch {
            message.error('正文下载失败')
        }
    }

    return (
        <div className='time-line__item-list-item'>
            <div className='time-line__item-list-item-title flex flex-between'>
                <span className='time-line__item-list-item-title-text'>{event.date}</span>
                <Divider style={{ borderBlockStart: '1px solid var(--border)', flex: 1 }} />
                <span className='time-line__item-list-item-dot'></span>
            </div>
            <div className='time-line__item-list-item-main'>
                <div className='time-line__item-list-item-main-line flex flex-between'>
                    <div className='time-line__item-list-item-time'>
                        <ClockCircleOutlined style={{ marginRight: 4 }} />{event.time}
                        <span className='time-line__item-list-item-sub-title'>{event.sub_title}</span>
                    </div>
                    <Tag variant='filled' className='text-10 is-accent'>{event.sourceLabel}</Tag>
                </div>
                {event.content && (
                    <div className='time-line__item-list-item-content'>{event.content}</div>
                )}
                <div className='time-line__item-list-item-footer'>
                    <div className='time-line__item-list-item-tabs'>
                        {event.tags.map((tab, idx) => (
                            <Tag key={idx} variant='filled' style={{ color: 'var(--text-3)' }} className='text-10'>
                                <FileOutlined style={{ fontSize: 9, marginRight: 4 }} />{tab}
                            </Tag>
                        ))}
                    </div>
                    <div className='time-line__item-list-item-actions'>
                        {event.source === 'filing' && event.entityId != null && (
                            <Button
                                type="link"
                                size="small"
                                icon={<PaperClipOutlined />}
                                onClick={() => setExpanded((v) => !v)}
                                className="time-line__item-action"
                            >
                                {expanded ? '收起附件' : '查看附件'}
                            </Button>
                        )}
                        {event.source === 'news' && event.entityId != null && (
                            <Button
                                type="link"
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={handleNewsDownload}
                                className="time-line__item-action"
                            >
                                下载正文
                            </Button>
                        )}
                    </div>
                </div>
                {expanded && event.source === 'filing' && event.entityId != null && (
                    <FilingFiles filingId={event.entityId} />
                )}
            </div>
        </div>
    )
}

// ── 主组件 ──────────────────────────────────────────────────

export default function TimeLine({ stockCode, onCountChange }: {
    stockCode: string
    onCountChange?: (n: number) => void
}) {
    const [activeSource, setActiveSource] = useState<SourceType>('filing')
    const [events, setEvents] = useState<TimelineEvent[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(false)
    // 分红/除权复权全量缓存（这两个接口不支持 offset 分页）
    const [fullCache, setFullCache] = useState<TimelineEvent[]>([])

    // fetch 函数只返回数据，不直接 setState（由 useEffect 统一检查 cancelled 后写入）
    const fetchFilingPage = useCallback(async (offset: number) => {
        const res = await getStockFilings(stockCode, { offset, limit: PAGE_SIZE })
        return { events: mapFilings(res.data), total: res.total }
    }, [stockCode])

    const fetchNewsPage = useCallback(async (offset: number) => {
        const res = await getStockNews(stockCode, { offset, limit: PAGE_SIZE })
        return { events: mapNews(res.data), total: res.total }
    }, [stockCode])

    // 切换标签或股票时：重置 page=1，拉取数据
    useEffect(() => {
        setPage(1)
        setLoading(true)
        let cancelled = false

        const offset = 0
        const promise: Promise<{ events: TimelineEvent[]; total: number; full?: TimelineEvent[] }> =
            activeSource === 'filing' ? fetchFilingPage(offset)
            : activeSource === 'news' ? fetchNewsPage(offset)
            : activeSource === 'dividend'
                ? getStockDividends(stockCode, 200).then((items) => {
                    const all = mapDividends(items).sort((a, b) => a.date < b.date ? 1 : -1)
                    return { events: all.slice(0, PAGE_SIZE), total: all.length, full: all }
                })
                : getStockCorporateActions(stockCode).then((items) => {
                    const all = mapCorpActions(items).sort((a, b) => a.date < b.date ? 1 : -1)
                    return { events: all.slice(0, PAGE_SIZE), total: all.length, full: all }
                })

        promise
            .then((result) => {
                if (cancelled) return
                setEvents(result.events)
                setTotal(result.total)
                if (result.full) setFullCache(result.full)
            })
            .catch(() => {
                if (cancelled) return
                setEvents([])
                setTotal(0)
                setFullCache([])
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSource, stockCode])

    // 翻页时（仅 page 变化，不重置标签）
    useEffect(() => {
        if (page === 1 && (activeSource === 'dividend' || activeSource === 'corp_action')) {
            return
        }
        setLoading(true)
        let cancelled = false
        const offset = (page - 1) * PAGE_SIZE

        const promise: Promise<{ events: TimelineEvent[]; total?: number }> =
            activeSource === 'filing' ? fetchFilingPage(offset)
            : activeSource === 'news' ? fetchNewsPage(offset)
            : Promise.resolve().then(() => ({
                events: fullCache.slice(offset, offset + PAGE_SIZE),
            }))

        promise
            .then((result) => {
                if (cancelled) return
                setEvents(result.events)
                if (result.total != null) setTotal(result.total)
            })
            .catch(() => {
                if (cancelled) return
                setEvents([])
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    // 上报 count
    useEffect(() => {
        onCountChange?.(total)
    }, [total, onCountChange])

    const handleTabChange = (source: SourceType) => {
        if (source === activeSource) return
        setFullCache([])
        setActiveSource(source)
    }

    const handlePageChange = (p: number) => {
        setPage(p)
    }

    return (
        <div className="time-line">
            <div className='time-line__title-tags'>
                {SOURCE_TABS.map((tab) => (
                    <Tag
                        key={tab.value}
                        className={`time-line__title-tag ${tab.value === activeSource ? 'is-active' : ''}`}
                        onClick={() => handleTabChange(tab.value)}
                    >
                        {tab.label}
                    </Tag>
                ))}
            </div>
            <Spin spinning={loading}>
                <div className="time-line__body">
                    {events.length === 0 && !loading ? (
                        <div style={{ padding: '40px 0' }}>
                            <Empty description="暂无事件" />
                        </div>
                    ) : (
                        <div className='time-line__item-list'>
                            {events.map((event) => (
                                <TimeLineItem key={event.id} event={event} />
                            ))}
                        </div>
                    )}
                </div>
            </Spin>
            {total > PAGE_SIZE && (
                <div className="time-line__pagination">
                    <Pagination
                        size="small"
                        current={page}
                        pageSize={PAGE_SIZE}
                        total={total}
                        showSizeChanger={false}
                        showTotal={(t) => `共 ${t} 条`}
                        onChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    )
}
